<template>
  <view-layout>
    <template #header v-if="$context.is.desktop">
      <threshold-coupon-message-bar />
    </template>
    <continue-action slot="action" v-if="!$context.is.desktop" />
    <zm-skeleton class="shopping-cart" slot="side" animated :rows="6" :loading="!ready">
      <shopping-cart
        :showTitle="$context.is.desktop"
        :filter-keys="draftPlanFilterKeys"
        @remove="(key, plan) => $refs.core.removePlanByCode(key, plan)"
      >
       <continue-action slot="action" v-if="$context.is.desktop" />
      </shopping-cart>
    </zm-skeleton>
    <div class="opc-config">
      <nav-cart-back
        class="opc-nav__cart-back"
        :visible="persistShoppingBagVisible"
        :back-content="$t('billing.common.cart_bag.continue_shopping_btn')"
        :back-handler="backHandler"
      />
      <div :class="`opc-config__title ${persistShoppingBagVisible ? 'opc-config__title-persist-cart' : ''}` ">
        <Nav />
      </div>
      <zm-skeleton animated :rows="10" :loading="!ready">
        <threshold-coupon-message-bar v-if="!$context.is.desktop" />
        <soft-bundle-alert-message />
        <open-invoice-alert />
        <upsell-banner class="upsell-biz-banner-config"/>
        <partner-refer />
        <BannerContainer />
        <core ref="core" :divider-text="enhanceTitle" :addonable="isAddonAllowed" />
        <ConfigDesc />
      </zm-skeleton>
    </div>
    <partner-promote />
  </view-layout>
</template>

<script>
import ViewLayout from "@/components/view-layout.vue";
import ViewLayoutMobile from "@/components/view-layout.m.vue";
import Nav from '@/components/nav.vue';
import ShoppingCart from './shopping-cart/index.vue';
import ContinueAction from './shopping-cart/continue.vue';
import Core from './core.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { BUNDLE_WITH_ZP_BASE_PLAN_TYPES, planTypes } from '@/common/constants';
import log from '@/common/log';
import rpsMixins from '@/aspects/rps';
import PartnerRefer from '@/views/config/partner-refer/index';
import BannerContainer from '@/components/service/config-banner';
import ConfigDesc from '@/components/service/config-desc';
import UpsellBanner from '@/views/config/upsell-banner/index.vue';
import {
  isZoneBundleContext,
  onlyPromotionZoneBundleForCampaign,
} from '@/plans/ZOOM_MEETING/common/utils';
import { $context } from '@/global-install';
import PartnerPromote from '@/views/succezz/components/partner-promote.vue';
import NavCartBack from '@/components/nav-cart-back.vue';
import persistShoppingBag, { persistCartConstant } from '@/views/config/shopping-cart/persist/persist-cart';
import { pageConfig } from '@/common/service/page-config';
import OpenInvoiceAlert from '@/views/config/shopping-cart/open-invoice/invoice-alert.vue';
import { isIAPRedirectUser } from '@/utils/iap'
import { PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import SoftBundleAlertMessage from '@/views/config/soft-bundle/components/alert-message.vue';
import ThresholdCouponMessageBar from '@/views/config/coupon/component/threshold-coupon/message-bar.vue';

export default {
  components: {
    OpenInvoiceAlert,
    NavCartBack,
    PartnerPromote,
    PartnerRefer,
    UpsellBanner,
    ViewLayout: $context.is.desktop ? ViewLayout : ViewLayoutMobile,
    ShoppingCart,
    ContinueAction,
    Core,
    Nav,
    BannerContainer,
    ConfigDesc,
    SoftBundleAlertMessage,
    ThresholdCouponMessageBar,
  },
  data() {
    return {
      ready: false,
      persistShoppingBagVisible: persistCartConstant.enablePersistShoppingBag && !isIAPRedirectUser
    };
  },
  mixins: [rpsMixins],
  computed: {
    ...mapGetters(['getCurrentBasePlanInfo', 'getSelectedPlans', 'selectedSingleUsePro', 'draftPlanFilterKeys','pastPlans']),
    basePlanType() {
      return this.getCurrentBasePlanInfo.planType;
    },
    selectedTypes() {
      let types = [...new Set(this.getSelectedPlans.map((x) => x.type))];
      const hasZoneBundleAndFromWinBack = this.pastPlans.some((x) => BUNDLE_WITH_ZP_BASE_PLAN_TYPES.includes(planTypes[x.semanticType])) && this.draftPlanFilterKeys.some(item => BUNDLE_WITH_ZP_BASE_PLAN_TYPES.includes(planTypes[item]));
      const hasOtherZp = this.getSelectedPlans.some(item => item.type === planTypes.ZOOM_PHONE && item.sub_type !== PBX_SUB_TYPES.BASE);

      // HARDCODE
      if ((hasZoneBundleAndFromWinBack && !hasOtherZp)  || (isZoneBundleContext && !hasOtherZp) || onlyPromotionZoneBundleForCampaign(this.getSelectedPlans)) types = types.filter(x => x !== planTypes.ZOOM_PHONE);
      return types;
    },
    enhanceTitle() {
      if (this.basePlanType === planTypes.ZOOM_PHONE) {
        return 'Enhance Your Zoom Phone Experience';
      }
      return this.$t('billing.enhance_experience');
    },
    isAddonAllowed() {
      const editType = pageConfig?.editConfig?.planType;
      if (editType === planTypes.ZOOM_SCHEDULER && this.$context.is.buyTrial && pageConfig?.enableBaseWithFreeTrialCheckout) {
        return true
      }
      if(this.$context.is.buyTrial || this.$context.is.add2trial) return false;
      if (this.selectedSingleUsePro) return false;
      if (this.$context.is.trial2paid) return false;
      return true;
    },
  },
  methods: {
    ...mapActions(['initPageFlow']),
    ...mapMutations(['setDraftPlanFilterKeys']),
    backHandler() {
      return persistShoppingBag.continueShopping();
    },
    async init() {
      await this.initPageFlow();
      this.setDraftPlanFilterKeys(planTypes.map(this.selectedTypes.length ? this.selectedTypes : [this.basePlanType]))
      this.ready = true;
      this.$nextTick(() => {
        this.focusToConfig()
      });
    },
    autoFocus(firstPanel) {
      const inputs = firstPanel.getElementsByTagName('input');
      if (!inputs || !inputs.length) return;

      for (let input of inputs) {
        if ((input.type === 'radio' || input.type === 'checkbox') && input.checked && !input.disabled) {
          input.focus();
          return;
        }
      }

      for (let input of inputs) {
        if (
          ['text', 'email', 'password', 'number', 'search', 'url', 'tel'].includes(input.type) &&
          !input.disabled &&
          input.offsetParent !== null
        ) {
          input.focus();
          return;
        }
      }

      const textareas = firstPanel.getElementsByTagName('textarea');
      if (textareas.length) {
        for (let ta of textareas) {
          if (!ta.disabled && ta.offsetParent !== null) {
            ta.focus();
            return;
          }
        }
      }

      for (let input of inputs) {
        if (typeof input.focus === 'function' && !input.disabled && input.offsetParent !== null) {
          input.focus();
          return;
        }
      }
    },
    focusToConfig() {
      const configPanels = document.getElementsByClassName('opc-foldable');
      const firstPanel = configPanels[0];
      if(!firstPanel) return;
      this.autoFocus(firstPanel);
    },
  },
  watch: {
    isAddonAllowed: {
      immediate: true,
      handler(is){
        log('config:addonable', is);
      }
    },

  },
  created() {
    this.init();

    this.$context.$on('update-draft-plan-keys', () => {
      this.$nextTick(() => {
        this.setDraftPlanFilterKeys(planTypes.map(this.selectedTypes.length ? this.selectedTypes : [this.basePlanType]))
      });
    });
  },
  mounted(){
    this.mx_asyncRpsBlock()
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';
.opc-config {
  width: 100%;
  &__title {
    margin-bottom: 48px;
    @include media(mobile,tablet){
      margin-bottom: $mobile-space;
    }
  }

  &__title-persist-cart {
    @include media(mobile,tablet) {
      display: none !important;
    }
  }
}
.config-desc-slot {
  color: var(--day-foreground-foreground-ada, #666487);
  /* Body/Body S */
  font-family: Almaden Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 21px */
}

.upsell-biz-banner-config {
  margin-top: -24px;
}

@include media(mobile, tablet) {
  .upsell-biz-banner-config.upsell-biz-banner-config {
    margin-top: 0;
  }
}


</style>
